import { ChevronDown, ChevronRight, InfoIcon } from 'lucide-react'
import React, { createContext, useContext, useState } from 'react'

import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { Button } from 'components/ui/button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
} from 'components/ui/item'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import { Badge } from 'slab'
import {
  type BrokersTaskExecutionContext,
  type BrokersTaskExecutionMethod,
  type BrokersTaskStatus,
  type BrokersWorkflowStatus,
} from 'utils/web_api.schemas'

import {
  ExecutionContextBadge,
  ExecutionMethodBadge,
  TaskStatusBadge,
  WorkflowStatusBadge,
} from './WorkflowVisualIndicators'

type ChildProps = {
  children?: React.ReactNode
}

type WorkflowStatusDoc = {
  status: BrokersWorkflowStatus
  description: string
  additionalInfo?: React.ReactNode
}

type TaskStatusDoc = {
  status: BrokersTaskStatus
  description: string
  additionalInfo?: React.ReactNode
}

type ExecutionMethodDoc = {
  method: BrokersTaskExecutionMethod
  description: string
  additionalInfo?: React.ReactNode
}

type ExecutionContextDoc = {
  context: BrokersTaskExecutionContext
  description: string
  additionalInfo?: React.ReactNode
}

// Documentation for each workflow status
const workflowStatusDocs: WorkflowStatusDoc[] = [
  {
    status: 'pending',
    description: 'Workflow has been created but has not started execution yet.',
  },
  {
    status: 'in_progress',
    description: 'Workflow is currently executing tasks.',
  },
  {
    status: 'completed',
    description: 'All tasks in the workflow have been completed successfully.',
  },
  {
    status: 'canceled',
    description: 'Workflow has been canceled and will not continue execution.',
    additionalInfo: (
      <div className='text-sm'>
        <p className='font-medium'>
          When a workflow is canceled, all tasks in an actionable state (e.g.
          pending, failed) will be canceled as well.
        </p>
      </div>
    ),
  },
  {
    status: 'paused',
    description: 'Workflow execution has been paused and requires attention.',
  },
]

// Documentation for each task status
const taskStatusDocs: TaskStatusDoc[] = [
  {
    status: 'pending',
    description: 'Task is waiting to be executed.',
    additionalInfo: (
      <div className='text-sm space-y-2'>
        <p className='font-medium'>
          For <b>Manual tasks:</b> generally this means the task is awaiting Ops
          to execute.
        </p>
        <p className='font-medium'>
          For <b>Automated tasks:</b> generally this means the task will be
          processed shortly. Most tasks immediately execute if a workflow is in
          progress.
        </p>
      </div>
    ),
  },
  {
    status: 'processing',
    description: 'Task is currently being processed.',
  },
  {
    status: 'completed',
    description: 'Task has been completed successfully.',
  },
  {
    status: 'failed',
    description: 'Task encountered an error and needs resolution.',
  },
  {
    status: 'canceled',
    description: 'Task has been canceled and will not be executed.',
    additionalInfo: (
      <div className='text-sm space-y-2'>
        <p className='font-medium'>Common reasons for task cancelation:</p>
        <ul className='list-disc pl-5 space-y-1'>
          <li>Workflow was canceled</li>
          <li>
            Logic in within the task has determined this work is no longer
            necessary
          </li>
        </ul>
        <p className='font-medium'>
          Generally you can check the message field of a task for more details
          on why it was canceled.
        </p>
      </div>
    ),
  },
  {
    status: 'excluded',
    description:
      'Task has been excluded from the workflow until an external event occurs.',
    additionalInfo: (
      <div className='text-sm space-y-2'>
        <p className='font-medium'>Common reasons for task exclusion:</p>
        <ul className='list-disc pl-5 space-y-1'>
          <li>Dependencies have not been completed</li>
          <li>Employer is pending review</li>
          <li>
            Waiting to deactivate payment method until enrollment is fully paid
            for
          </li>
        </ul>
      </div>
    ),
  },
]

// Documentation for each execution method
const executionMethodDocs: ExecutionMethodDoc[] = [
  {
    method: 'automated',
    description: 'Task will be executed automatically by our automation.',
  },
  {
    method: 'manual',
    description: 'Task requires manual intervention to complete.',
  },
]

// Documentation for each execution context
const executionContextDocs: ExecutionContextDoc[] = [
  {
    context: 'thatch',
    description: 'Task is executed internally by Thatch.',
    additionalInfo: (
      <div className='text-sm space-y-2'>
        <p className='font-medium'>
          Tasks that are executed by Thatch don't have a badge in product in
          most contexts since its assumed to be the default execution context.
        </p>
      </div>
    ),
  },
  {
    context: 'member',
    description: 'Task requires action by the member via a task displayed within their dashboard.',
  },
]

export const WorkflowStatusDocumentationContext = createContext<{
  isOpen: boolean
  openDocumentation: () => void
  closeDocumentation: () => void
}>({
      isOpen: false,
      openDocumentation: () => {},
      closeDocumentation: () => {},
    })

function useWorkflowStatusDocumentationProvider () {
  const [isOpen, setIsOpen] = useState(false)

  function openDocumentation () {
    setIsOpen(true)
  }

  function closeDocumentation () {
    setIsOpen(false)
  }

  return {
    isOpen,
    openDocumentation,
    closeDocumentation,
  }
}

export function WorkflowStatusDocumentationProvider ({ children }: ChildProps) {
  const value = useWorkflowStatusDocumentationProvider()

  return (
    <WorkflowStatusDocumentationContext.Provider value={value}>
      {children}
    </WorkflowStatusDocumentationContext.Provider>
  )
}

export function useWorkflowStatusDocumentation () {
  return useContext(WorkflowStatusDocumentationContext)
}

// Reusable component for displaying status documentation with optional expandable section
function StatusDocItem ({
  badge,
  description,
  additionalInfo,
}: {
  badge: React.ReactNode
  description: string
  additionalInfo?: React.ReactNode
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Item variant='outline' size='sm' className='gap-0'>
      <div className='flex items-center gap-2.5 w-full'>
        <ItemMedia className='min-w-28 flex justify-start'>{badge}</ItemMedia>
        <ItemContent>
          <ItemDescription className='line-clamp-none'>
            {description}
          </ItemDescription>
        </ItemContent>
        {additionalInfo && (
          <ItemActions>
            <TooltipWrapper
              tooltipText={isExpanded ? 'Hide details' : 'Show more details'}
              side='top'
              contentClasses='z-[60]'
            >
              <Button
                variant='ghost'
                onClick={() => setIsExpanded(!isExpanded)}
                className='h-8 w-8 p-0'
              >
                {isExpanded
                  ? (
                    <ChevronDown size={16} />
                    )
                  : (
                    <ChevronRight size={16} />
                    )}
              </Button>
            </TooltipWrapper>
          </ItemActions>
        )}
      </div>
      {additionalInfo && (
        <div className='basis-full'>
          <AnimateHeightAndOpacity isOpen={isExpanded}>
            <div className='flex items-center justify-between w-full mt-2 pb-4'>
              <div className='pl-2 border-l-2 border-gray-300'>
                {additionalInfo}
              </div>
            </div>
          </AnimateHeightAndOpacity>
        </div>
      )}
    </Item>
  )
}

export function WorkflowStatusDocumentationSheet () {
  const { isOpen, closeDocumentation } = useWorkflowStatusDocumentation()

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeDocumentation()
    }
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent responsiveLayout className='xs:max-w-2xl'>
          <SheetHeader>
            <SheetTitle className='pb-4'>
              <span className='heading-lg'>Workflow & Task Statuses</span>
            </SheetTitle>
          </SheetHeader>
          <SheetDescription className='mb-4'>
            Below are descriptions of all the workflow and task related
            statuses. Click on the button on the right for more context.
          </SheetDescription>

          <div className='space-y-6'>
            {/* Workflow Statuses Section */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>Workflow Statuses</h3>
              <ItemGroup className='gap-3'>
                {workflowStatusDocs.map((doc) => (
                  <StatusDocItem
                    key={doc.status}
                    badge={<WorkflowStatusBadge status={doc.status} />}
                    description={doc.description}
                    additionalInfo={doc.additionalInfo}
                  />
                ))}
              </ItemGroup>
            </div>

            {/* Task Statuses Section */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>Task Statuses</h3>
              <ItemGroup className='gap-3'>
                {taskStatusDocs.map((doc, index) => (
                  <StatusDocItem
                    key={`${doc.status}-${index}`}
                    badge={
                      <TaskStatusBadge
                        status={doc.status}
                        isExecutable={false}
                      />
                    }
                    description={doc.description}
                    additionalInfo={doc.additionalInfo}
                  />
                ))}
              </ItemGroup>
            </div>

            {/* Task Execution Method Section */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Task Execution Methods
              </h3>
              <ItemGroup className='gap-3'>
                {executionMethodDocs.map((doc) => (
                  <StatusDocItem
                    key={doc.method}
                    badge={<ExecutionMethodBadge method={doc.method} />}
                    description={doc.description}
                    additionalInfo={doc.additionalInfo}
                  />
                ))}
              </ItemGroup>
            </div>

            {/* Task Execution Context Section */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Task Execution Context
              </h3>
              <ItemGroup className='gap-3'>
                {executionContextDocs.map((doc) => (
                  <StatusDocItem
                    key={doc.context}
                    // We don't actually display a badge Thatch execution context generally.
                    badge={
                      doc.context === 'thatch'
                        ? (
                          <Badge type='slate' bordered>
                            Internal
                          </Badge>
                          )
                        : (
                          <ExecutionContextBadge context={doc.context} />
                          )
                    }
                    description={doc.description}
                    additionalInfo={doc.additionalInfo}
                  />
                ))}
              </ItemGroup>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

// Clickable info icon trigger component
export function WorkflowStatusDocumentationTrigger () {
  const { openDocumentation } = useWorkflowStatusDocumentation()

  return (
    <TooltipWrapper tooltipText='View workflow status documentation' side='top'>
      <InfoIcon
        size={16}
        className='ml-1 inline-block cursor-pointer text-gray-500 hover:text-gray-700'
        onClick={(e) => {
          e.stopPropagation()
          openDocumentation()
        }}
      />
    </TooltipWrapper>
  )
}
